import { useEffect, useMemo, useState } from 'react';
import { AppDialogFrame } from '../../../components/overlays/AppDialogFrame';
import { SupportErrorMessage } from '../../../components/overlays/EngineeringSupportBanner';
import type { TuitionFamilySnapshot } from '../shared/tuitionData';
import {
  useAssignRecurringFeeMutation,
  useListRecurringFeeAssignmentsQuery,
  useListRecurringFeeDefinitionsQuery,
  useUnassignRecurringFeeMutation,
} from '../../../state/domainRouterApi';

type RecurringFeeBillingCategory =
  | 'activities'
  | 'transportation'
  | 'food_service'
  | 'other';

type RecurringFeeDefinitionItem = {
  id: string;
  name: string;
  monthlyAmount: number;
  billingCategory: RecurringFeeBillingCategory;
  eligibleGrades: string[];
};

type RecurringFeeAssignmentItem = {
  id: string;
  recurringFeeId: string;
  studentId: string;
  tuitionAccountId: string;
  customAmount: number | null;
  startDate: string | null;
};

type DraftValue = {
  assigned: boolean;
  customAmount: string;
};

type Props = {
  isOpen: boolean;
  family: TuitionFamilySnapshot | null;
  onClose: () => void;
  onSaved?: (message: string) => void;
};

const normalizeDefinitions = (items: unknown[] | undefined): RecurringFeeDefinitionItem[] =>
  (items ?? []).map((item) => {
    const record = item as Record<string, unknown>;
    return {
      id: String(record.id ?? ''),
      name: String(record.name ?? ''),
      monthlyAmount: Number(record.monthlyAmount ?? 0),
      billingCategory: (
        record.billingCategory === 'transportation'
        || record.billingCategory === 'food_service'
        || record.billingCategory === 'other'
          ? record.billingCategory
          : 'activities'
      ) as RecurringFeeBillingCategory,
      eligibleGrades: Array.isArray(record.eligibleGrades)
        ? record.eligibleGrades.map((value) => String(value)).filter(Boolean)
        : [],
    };
  }).filter((item) => item.id && item.name);

const normalizeAssignments = (items: unknown[] | undefined): RecurringFeeAssignmentItem[] =>
  (items ?? []).map((item) => {
    const record = item as Record<string, unknown>;
    return {
      id: String(record.id ?? ''),
      recurringFeeId: String(record.recurringFeeId ?? ''),
      studentId: String(record.studentId ?? ''),
      tuitionAccountId: String(record.tuitionAccountId ?? ''),
      customAmount: record.customAmount == null ? null : Number(record.customAmount),
      startDate: record.startDate == null ? null : String(record.startDate),
    };
  }).filter((item) => item.id && item.recurringFeeId && item.studentId);

const makeDraftKey = (studentId: string, recurringFeeId: string): string =>
  `${studentId}::${recurringFeeId}`;

const formatCurrency = (amount: number): string =>
  `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const isGradeEligible = (gradeLabel: string, definition: RecurringFeeDefinitionItem): boolean => {
  if (definition.eligibleGrades.length === 0) {
    return true;
  }
  const normalizedGrade = gradeLabel.trim().toLowerCase();
  return definition.eligibleGrades.some((grade) => grade.trim().toLowerCase() === normalizedGrade);
};

export function RecurringFeeAssignment({
  isOpen,
  family,
  onClose,
  onSaved,
}: Props) {
  const definitionsQuery = useListRecurringFeeDefinitionsQuery(undefined, {
    skip: !isOpen,
  });
  const assignmentsQuery = useListRecurringFeeAssignmentsQuery(
    {
      tuitionAccountId: family?.familyId ?? '',
      isActive: true,
    },
    {
      skip: !isOpen || !family,
    },
  );
  const [assignRecurringFee, assignRecurringFeeState] = useAssignRecurringFeeMutation();
  const [unassignRecurringFee, unassignRecurringFeeState] = useUnassignRecurringFeeMutation();
  const [drafts, setDrafts] = useState<Record<string, DraftValue>>({});
  const [error, setError] = useState<string | null>(null);
  const [selectedStudentId, setSelectedStudentId] = useState('');

  const definitions = useMemo(
    () => normalizeDefinitions(definitionsQuery.data?.items),
    [definitionsQuery.data?.items],
  );
  const assignments = useMemo(
    () => normalizeAssignments(assignmentsQuery.data?.items),
    [assignmentsQuery.data?.items],
  );
  const assignmentsByKey = useMemo(
    () => new Map(assignments.map((assignment) => [makeDraftKey(assignment.studentId, assignment.recurringFeeId), assignment])),
    [assignments],
  );

  const busy = assignRecurringFeeState.isLoading
    || unassignRecurringFeeState.isLoading;

  useEffect(() => {
    if (!isOpen || !family) {
      setDrafts({});
      setError(null);
      setSelectedStudentId('');
      return;
    }

    const nextDrafts: Record<string, DraftValue> = {};
    for (const student of family.students) {
      for (const definition of definitions) {
        if (!isGradeEligible(student.gradeLabel, definition)) {
          continue;
        }
        const assignment = assignmentsByKey.get(makeDraftKey(student.id, definition.id));
        nextDrafts[makeDraftKey(student.id, definition.id)] = {
          assigned: Boolean(assignment),
          customAmount: assignment?.customAmount != null ? String(assignment.customAmount) : '',
        };
      }
    }
    setDrafts(nextDrafts);
    setError(null);
    setSelectedStudentId((current) => (
      current && family.students.some((student) => student.id === current)
        ? current
        : family.students[0]?.id ?? ''
    ));
  }, [assignmentsByKey, definitions, family, isOpen]);

  const updateDraft = (studentId: string, recurringFeeId: string, patch: Partial<DraftValue>) => {
    const key = makeDraftKey(studentId, recurringFeeId);
    setDrafts((current) => ({
      ...current,
      [key]: {
        assigned: current[key]?.assigned ?? false,
        customAmount: current[key]?.customAmount ?? '',
        ...patch,
      },
    }));
  };

  const handleSave = async () => {
    if (!family) {
      return;
    }

    setError(null);
    let changeCount = 0;

    try {
      for (const student of family.students) {
        for (const definition of definitions) {
          if (!isGradeEligible(student.gradeLabel, definition)) {
            continue;
          }

          const key = makeDraftKey(student.id, definition.id);
          const draft = drafts[key] ?? { assigned: false, customAmount: '' };
          const existingAssignment = assignmentsByKey.get(key);
          const parsedCustomAmount = draft.customAmount.trim() ? Number(draft.customAmount) : null;

          if (draft.customAmount.trim() && (!Number.isFinite(parsedCustomAmount) || Number(parsedCustomAmount) <= 0)) {
            throw new Error(`Custom amount for ${student.name} must be greater than zero.`);
          }

          if (draft.assigned && !existingAssignment) {
            await assignRecurringFee({
              recurringFeeId: definition.id,
              studentId: student.id,
              tuitionAccountId: family.familyId,
              customAmount: parsedCustomAmount,
            }).unwrap();
            changeCount += 1;
            continue;
          }

          if (!draft.assigned && existingAssignment) {
            await unassignRecurringFee({
              assignmentId: existingAssignment.id,
            }).unwrap();
            changeCount += 1;
            continue;
          }

          if (draft.assigned && existingAssignment) {
            const existingCustomAmount = existingAssignment.customAmount == null ? '' : String(existingAssignment.customAmount);
            if (existingCustomAmount !== draft.customAmount.trim()) {
              await assignRecurringFee({
                recurringFeeId: definition.id,
                studentId: student.id,
                tuitionAccountId: family.familyId,
                customAmount: parsedCustomAmount,
                startDate: existingAssignment.startDate ?? undefined,
              }).unwrap();
              changeCount += 1;
            }
          }
        }
      }

      await assignmentsQuery.refetch();
      onSaved?.(
        changeCount > 0
          ? `Saved recurring fee assignments for ${family.familyName}.`
          : `No recurring fee changes were needed for ${family.familyName}.`,
      );
      onClose();
    } catch (saveError) {
      setError(
        saveError && typeof saveError === 'object' && 'message' in saveError
          ? String(saveError.message)
          : 'Unable to save recurring fee assignments.',
      );
    }
  };

  const loading = definitionsQuery.isLoading || assignmentsQuery.isLoading;
  const activeStudent = family?.students.find((student) => student.id === selectedStudentId) ?? family?.students[0] ?? null;
  const activeStudentDefinitions = useMemo(
    () => activeStudent
      ? definitions.filter((definition) => isGradeEligible(activeStudent.gradeLabel, definition))
      : [],
    [activeStudent, definitions],
  );

  return (
    <AppDialogFrame
      open={isOpen}
      onClose={onClose}
      title={family ? `Manage recurring fees — ${family.familyName}` : 'Manage recurring fees'}
      maxWidthClassName="max-w-[700px]"
      panelClassName="rounded-[18px] border-[#e7edf3] bg-white shadow-[0_24px_60px_rgba(16,24,40,0.16)]"
      headerClassName="border-[#e7edf3] px-5 py-4"
      bodyPaddingClassName="p-0"
      footerPaddingClassName="px-5 py-4"
      footerClassName="border-[#e7edf3] bg-white"
      titleClassName="font-title text-[18px] font-semibold text-iron-grey"
      footer={(
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-[8px] border border-[#d8dee6] bg-white px-5 py-2.5 text-[15px] font-medium text-iron-grey transition-colors hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              void handleSave();
            }}
            disabled={busy || loading}
            className="rounded-[8px] bg-[#2697a2] px-5 py-2.5 text-[15px] font-semibold text-white transition-colors hover:bg-[#1d7e87] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? 'Saving...' : 'Save assignments'}
          </button>
        </div>
      )}
    >
      {!family ? null : (
        <div className="flex min-h-[360px]">
          <aside className="w-[198px] shrink-0 border-r border-[#e7edf3] bg-white">
            {family.students.map((student) => {
              const selected = student.id === activeStudent?.id;
              return (
                <button
                  key={student.id}
                  type="button"
                  onClick={() => setSelectedStudentId(student.id)}
                  className={`flex w-full flex-col items-start border-b border-[#eef2f6] px-4 py-3 text-left transition-colors last:border-b-0 ${
                    selected ? 'bg-white' : 'bg-white hover:bg-[#f8fafc]'
                  }`}
                >
                  <span className={`text-[14px] font-semibold ${selected ? 'text-[#2697a2]' : 'text-iron-grey'}`}>
                    {student.name}
                  </span>
                  <span className="mt-1 text-[13px] text-text-secondary">
                    {student.gradeLabel}
                  </span>
                </button>
              );
            })}
          </aside>

          <div className="flex-1 bg-[#fbfdff] p-6">
            {error ? (
              <SupportErrorMessage className="mb-4">
                {error}
              </SupportErrorMessage>
            ) : null}

            {loading ? (
              <div className="space-y-4">
                <div className="h-6 w-56 animate-pulse rounded bg-muted" />
                <div className="h-4 w-44 animate-pulse rounded bg-muted" />
                {Array.from({ length: 2 }).map((_, index) => (
                  <div key={index} className="h-20 animate-pulse rounded-[12px] border border-[#e7edf3] bg-white" />
                ))}
              </div>
            ) : !activeStudent ? null : (
              <div className="space-y-5">
                <div>
                  <h3 className="text-[16px] font-semibold text-iron-grey">
                    Fees for {activeStudent.name}
                  </h3>
                  <p className="mt-1 text-[14px] text-text-secondary">
                    Assign recurring monthly charges.
                  </p>
                </div>

                {activeStudentDefinitions.length === 0 ? (
                  <div className="rounded-[12px] border border-[#e7edf3] bg-white px-4 py-4 text-sm text-text-secondary">
                    No recurring fees match this student&apos;s current grade.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {activeStudentDefinitions.map((definition) => {
                      const key = makeDraftKey(activeStudent.id, definition.id);
                      const draft = drafts[key] ?? { assigned: false, customAmount: '' };
                      return (
                        <div
                          key={definition.id}
                          className="rounded-[12px] border border-[#dbe4ec] bg-white px-4 py-4 shadow-[0_1px_2px_rgba(16,24,40,0.03)]"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0">
                              <p className="text-[15px] font-semibold text-iron-grey">
                                {definition.name}
                              </p>
                              <p className="mt-1 text-[14px] text-text-secondary">
                                Default: {formatCurrency(definition.monthlyAmount)} / month
                              </p>
                            </div>

                            <button
                              type="button"
                              onClick={() => updateDraft(activeStudent.id, definition.id, { assigned: !draft.assigned })}
                              aria-pressed={draft.assigned}
                              aria-label={`${draft.assigned ? 'Disable' : 'Enable'} ${definition.name}`}
                              className={`relative inline-flex h-8 w-[44px] shrink-0 items-center rounded-full transition-colors ${
                                draft.assigned ? 'bg-[#2697a2]' : 'bg-[#cfd6df]'
                              }`}
                            >
                              <span
                                className={`inline-block h-6 w-6 rounded-full bg-white shadow-sm transition-transform ${
                                  draft.assigned ? 'translate-x-[18px]' : 'translate-x-[2px]'
                                }`}
                              />
                            </button>
                          </div>

                          {draft.assigned ? (
                            <div className="mt-4 max-w-[240px]">
                              <label className="mb-1.5 block text-[12px] font-medium uppercase tracking-[0.04em] text-text-secondary">
                                Custom monthly amount
                              </label>
                              <div className="flex items-center rounded-[10px] border border-[#d8dee6] bg-[#f8fafc] px-3">
                                <span className="text-sm text-text-secondary">$</span>
                                <input
                                  type="number"
                                  min="0.01"
                                  step="0.01"
                                  value={draft.customAmount}
                                  onChange={(event) => updateDraft(activeStudent.id, definition.id, { customAmount: event.target.value })}
                                  placeholder={definition.monthlyAmount.toFixed(2)}
                                  className="w-full bg-transparent px-2 py-2.5 text-sm text-iron-grey outline-none"
                                />
                              </div>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </AppDialogFrame>
  );
}
